import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { ContactCheckboxes, useTriSelection, type EntityContact, EMPTY_CONTACT, type TriValues } from './ContactCheckboxes'

interface VoiceFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  contact?: EntityContact
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Send Voice action (CRMService/SendVoiceEnquiry). */
export function VoiceForm({ entityId, entityType = 'lead', contact = EMPTY_CONTACT, onSuccess }: VoiceFormProps) {
  const [didNumbers, setDidNumbers] = useState<Option[]>([])
  const [appNames, setAppNames] = useState<Option[]>([])

  const [dnidNo, setDnidNo] = useState<string>('')
  const [appFlow, setAppFlow] = useState<string>('')
  const [duration, setDuration] = useState<string>('30')
  const [route, setRoute] = useState<string>('')

  const mobileValues: TriValues = [contact.mobileNo, contact.mobileNo1, contact.mobileNo2]
  const mobiles = useTriSelection(mobileValues)

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const [dids, apps] = await Promise.all([api.fetchDidNumbers(), api.fetchAppNames()])
        if (!active) return
        setDidNumbers(dids)
        setAppNames(apps)
      } catch {
        if (active) setError('Failed to load voice settings.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  const handleReset = () => {
    setDnidNo('')
    setAppFlow('')
    setDuration('30')
    setRoute('')
    mobiles.reset()
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!dnidNo && !appFlow) {
      setError('Please select a DNID or an App Flow.')
      return
    }
    if (!mobiles.anySelected) {
      setError('Please select at least one mobile number.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitVoice({
        entityId,
        entityType,
        mobileNo: mobiles.selected[0],
        mobileNo1: mobiles.selected[1],
        mobileNo2: mobiles.selected[2],
        dnidNo,
        appFlow,
        duration: String(Number(duration) || 30),
        route,
        voiceSource: 'CRM',
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to trigger voice call.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading Voice settings…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="vc-dnid">
          DNID
        </label>
        <select
          id="vc-dnid"
          className="form-control"
          value={dnidNo}
          onChange={(e) => setDnidNo(e.target.value)}
        >
          <option value="">-- Select DNID --</option>
          {didNumbers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="vc-appflow">
          App Flow / IVR
        </label>
        <select
          id="vc-appflow"
          className="form-control"
          value={appFlow}
          onChange={(e) => setAppFlow(e.target.value)}
        >
          <option value="">-- Select App Flow --</option>
          {appNames.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="vc-duration">
          Duration (seconds)
        </label>
        <input
          id="vc-duration"
          type="number"
          className="form-control"
          min={1}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="vc-route">
          Route
        </label>
        <input
          id="vc-route"
          type="text"
          className="form-control"
          value={route}
          onChange={(e) => setRoute(e.target.value)}
          placeholder="Route"
        />
      </div>

      <ContactCheckboxes
        label="Mobile Number(s)"
        idPrefix="vc-mobile"
        values={mobileValues}
        checked={mobiles.checked}
        onToggle={mobiles.toggle}
        emptyMessage="This record has no mobile number."
      />

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Dialling…' : 'Send Voice / Call'}
        </button>
      </div>
    </div>
  )
}
